import React, { useState } from 'react';
import {
  Drawer,
  List,
  IconButton,
  Divider,
  Box,
  Typography,
  Tooltip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';

import MenuIcon from '@mui/icons-material/Menu';

import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DnsIcon from '@mui/icons-material/Dns';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import CategoryIcon from '@mui/icons-material/Category';
import SeoIcon from '@mui/icons-material/Search';
import SwapCallsIcon from '@mui/icons-material/SwapCalls';
import CloudIcon from '@mui/icons-material/Cloud';
import GoogleIcon from '@mui/icons-material/Google';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SubscriptionsSharpIcon from '@mui/icons-material/SubscriptionsSharp';
import StyleIcon from '@mui/icons-material/Style';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

import { Link, useLocation } from 'react-router-dom';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';

// Icon colors
const iconColors = {
  dashboard: '#4CAF50', // Green
  shoppingCart: '#FF5722', // Deep Orange
  storage: '#607D8B', // Blue Grey
  people: '#9C27B0', // Purple
  cloudUpload: '#00BCD4', // Cyan
  listAlt: '#795548', // Brown
  contactPage: '#3F51B5', // Indigo
  groupAdd: '#FF9800', // Orange
  adminPanel: '#F44336', // Red
  dns: '#009688', // Teal
  folderShared: '#8BC34A', // Light Green
  category: '#E91E63', // Pink
  seo: '#673AB7', // Deep Purple
  sitemap: '#1d7272', // Yellow
  swapCalls: '#CDDC39', // Lime
  cloud: '#2196F3', // Blue
  google: '#4285F4', // Google Blue
  creditCard: '#FFC107', // Amber
  subscriptions: '#9E9E9E', // Grey
  style: '#00ACC1', // Cyan 600
  shoppingBag: '#43A047', // Green
};

// SidebarItem component
const SidebarItem = ({ to, icon, label, isOpen, onClick, iconColor }) => {
  const location = useLocation();
  const isActive = location.pathname === to || (to.length > 1 && location.pathname.startsWith(`${to}/`));

  return (
    <ListItemButton
      component={Link}
      to={to}
      onClick={onClick}
      sx={{
        pl: isOpen ? 3 : 2,
        py: 1,
        color: isActive ? 'white' : 'black',
        backgroundColor: isActive ? '#1976d2' : 'transparent',
        borderRadius: 1,
        '&:hover': {
          backgroundColor: '#1565c0',
          color: 'white',
        },
        whiteSpace: 'nowrap',
      }}
    >
      <ListItemIcon sx={{ color: isActive ? 'white' : iconColor, minWidth: 36 }}>
        {React.cloneElement(icon, { style: { color: isActive ? 'white' : iconColor } })}
      </ListItemIcon>
      {isOpen && (
        <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 500 }} />
      )}
    </ListItemButton>
  );
};

// SidebarSection component
const SidebarSection = ({ label, icon, children, open, toggleOpen, isOpen, iconColor }) => (
  <>
    <ListItemButton
      onClick={toggleOpen}
      sx={{
        pl: isOpen ? 3 : 2,
        py: 1,
        color: 'black',
        justifyContent: isOpen ? 'flex-start' : 'center',
        borderRadius: 1,
        '&:hover': {
          backgroundColor: '#f0f0f0',
        },
        whiteSpace: 'nowrap',
      }}
    >
      <ListItemIcon sx={{ color: iconColor, minWidth: 36 }}>
        {React.cloneElement(icon, { style: { color: iconColor } })}
      </ListItemIcon>
      {isOpen && (
        <>
          <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 500 }} />
          {open ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
        </>
      )}
    </ListItemButton>

    <Collapse in={open} timeout="auto" unmountOnExit>
      <List
        component="div"
        disablePadding
        sx={{
          mt: 1,
          mb: 1,
          pl: isOpen ? 4 : 0,
        }}
      >
        {children}
      </List>
    </Collapse>
  </>
);

const Sidebar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(!isMobile);
  const [dataAdminOpen, setDataAdminOpen] = useState(false);
  const [sampleAdminOpen, setSampleAdminOpen] = useState(false);
  const [productCategories, setProductCategories] = useState(false);
  const [seoToolsOpen, setSeoToolsOpen] = useState(false);
  const [connectedAppsOpen, setConnectedAppsOpen] = useState(false);
  const [subscriptionsModelsOpen, setSubscriptionsModelsOpen] = useState(false);
  const [registeredUsersOpen, setRegisteredUsersOpen] = useState(false);
  const { user } = useSelector((state) => state.user);

  const toggleSidebar = () => setIsOpen((prev) => !prev);

  const handleItemClick = () => {
    if (isMobile) setIsOpen(false);
  };

  const renderItem = (to, icon, label, color) => (
    <Tooltip title={isOpen ? '' : label} placement="right" disableHoverListener={isOpen}>
      <div>
        <SidebarItem
          to={to}
          icon={icon}
          label={label}
          isOpen={isOpen}
          onClick={handleItemClick}
          iconColor={color}
        />
      </div>
    </Tooltip>
  );

  return (
    <>
      {/* Toggle Button for Mobile */}
      {isMobile && (
        <IconButton
          onClick={toggleSidebar}
          sx={{
            position: 'fixed',
            top: 10,
            left: 10,
            zIndex: 1301,
            color: '#1976d2',
            backgroundColor: 'white',
            boxShadow: '0 0 5px rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: '#e3f2fd',
            },
          }}
          size="large"
          aria-label="toggle sidebar"
        >
          <MenuIcon />
        </IconButton>
      )}

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          width: isOpen ? 250 : 72,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isOpen ? 250 : 72,
            top: isMobile ? 0 : 64,
            height: isMobile ? '100%' : 'calc(100% - 64px)',
            paddingBottom: 2,
            backgroundColor: 'white',
            color: 'black',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)',
            borderRight: '1px solid #ddd',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isOpen ? 'space-between' : 'center',
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            minHeight: 64,
          }}
        >
          {isOpen && (
            <Typography variant="h6" sx={{ color: 'black', fontWeight: 600, userSelect: 'none' }}>
              Admin Panel
            </Typography>
          )}
          {!isMobile && (
            <IconButton onClick={toggleSidebar} sx={{ color: '#1976d2' }} size="large" aria-label="toggle sidebar">
              <MenuIcon />
            </IconButton>
          )}
        </Box>

        <Divider />

        <List
          sx={{
            color: 'black',
            '& .MuiListItem-root:hover': {
              backgroundColor: '#1976d2',
              color: 'white',
              transition: 'all 0.3s ease',
            },
            '& .MuiListItem-root:hover svg': {
              color: 'white',
            },
          }}
        >
          {renderItem('/', <DashboardIcon />, 'Overview', iconColors.dashboard)}
          {renderItem('/dashboard/exhibitor-productlist', <ShoppingCartIcon />, 'Exhibitor Products', iconColors.shoppingCart)}
          {renderItem('/dashboard/sanctionsdatabase-datacount', <StorageIcon />, 'Sanctions Database', iconColors.storage)}

          <Divider sx={{ my: 1 }} />

          <SidebarSection
            label="User Lists"
            icon={<PeopleIcon />}
            open={sampleAdminOpen}
            toggleOpen={() => setSampleAdminOpen(!sampleAdminOpen)}
            isOpen={isOpen}
            iconColor={iconColors.people}
          >
            {renderItem('/dashboard/download-sample-user', <CloudUploadIcon />, 'Download Sample', iconColors.cloudUpload)}
            {renderItem('/dashboard/add-event-user-list', <ListAltIcon />, 'Add Event List', iconColors.listAlt)}
            {renderItem('/dashboard/contact-user-list', <ContactPageIcon />, 'Contact List', iconColors.contactPage)}
          </SidebarSection>

          <SidebarSection
            label="Registered Users"
            icon={<GroupAddIcon />}
            open={registeredUsersOpen}
            toggleOpen={() => setRegisteredUsersOpen(!registeredUsersOpen)}
            isOpen={isOpen}
            iconColor={iconColors.groupAdd}
          >
            {renderItem('/dashboard/customers', <PeopleIcon />, 'Customers', iconColors.people)}
            {renderItem('/dashboard/register-user-list', <GroupAddIcon />, 'Products Purchases', iconColors.groupAdd)}
            {renderItem('/dashboard/plan-purchases', <ShoppingBagIcon />, 'Credit Purchases', iconColors.shoppingBag)}
          </SidebarSection>
          {renderItem('/dashboard/admin-user', <AdminPanelSettingsIcon />, 'Admin User', iconColors.adminPanel)}

          <Divider sx={{ my: 1 }} />

          <SidebarSection
            label="Data Admin"
            icon={<DnsIcon />}
            open={dataAdminOpen}
            toggleOpen={() => setDataAdminOpen(!dataAdminOpen)}
            isOpen={isOpen}
            iconColor={iconColors.dns}
          >
            {renderItem('/dashboard/upload-data', <CloudUploadIcon />, 'Upload Data', iconColors.cloudUpload)}
            {renderItem('/dashboard/data-store', <FolderSharedIcon />, 'Data Store', iconColors.folderShared)}
          </SidebarSection>

          <Divider sx={{ my: 1 }} />

          <SidebarSection
            label="Categories"
            icon={<StyleIcon />}
            open={productCategories}
            toggleOpen={() => setProductCategories(!productCategories)}
            isOpen={isOpen}
            iconColor={iconColors.style}
          >
            {renderItem('/dashboard/categories', <CategoryIcon />, 'Categories', iconColors.category)}
          </SidebarSection>

          <Divider sx={{ my: 1 }} />

          <SidebarSection
            label="SEO Tools"
            icon={<SeoIcon />}
            open={seoToolsOpen}
            toggleOpen={() => setSeoToolsOpen(!seoToolsOpen)}
            isOpen={isOpen}
            iconColor={iconColors.seo}
          >
            {renderItem('/dashboard/redirection', <SwapCallsIcon />, 'Redirections', iconColors.swapCalls)}
          </SidebarSection>

          <Divider sx={{ my: 1 }} />

          {renderItem('/dashboard/sitemap', <AccountTreeIcon />, 'Sitemap', iconColors.sitemap)}

          <Divider sx={{ my: 1 }} />

          <SidebarSection
            label="Connected Apps"
            icon={<CloudIcon />}
            open={connectedAppsOpen}
            toggleOpen={() => setConnectedAppsOpen(!connectedAppsOpen)}
            isOpen={isOpen}
            iconColor={iconColors.cloud}
          >
            {renderItem('/dashboard/connected-apps/google-merchent', <GoogleIcon />, 'Google Merchant', iconColors.google)}
          </SidebarSection>

          <Divider sx={{ my: 1 }} />

          <SidebarSection
            label="Subscriptions Models"
            icon={<CreditCardIcon />}
            open={subscriptionsModelsOpen}
            toggleOpen={() => setSubscriptionsModelsOpen(!subscriptionsModelsOpen)}
            isOpen={isOpen}
            iconColor={iconColors.creditCard}
          >
            {renderItem('/dashboard/subscriptions-models', <SubscriptionsSharpIcon />, 'Subscriptions Plans', iconColors.subscriptions)}
          </SidebarSection>
        </List>
      </Drawer>
    </>
  );
};

export default Sidebar;